import React, { useState, useEffect, useRef, useMemo } from "react";
import Bedroom from "./Bedroom";
import DiningRoom from "./DiningRoom";
import Hallway from "./Hallway";
import BTS from "./BTS";
import Performances from "./Performances";
import LivingRoom from "./LivingRoom";

import FloorplanSVG from "./FloorplanSVG";
import './FloorPlan.css';

// --- added hook ---
function usePreloadImages(urls = []) {
    useEffect(() => {
        if (!urls.length) return;
        urls.forEach((url) => {
            const img = new Image();
            img.src = url;
        });
    }, [urls]);
}

const HIGHLIGHT_MAP = {
  "E601FF": '/layout/bedroom-highlight_result.webp',
  "FE7902": '/layout/living-room-highlight_result.webp',
  "3CFE02": '/layout/bts-highlight_result.webp',
  "02CEFF": '/layout/dining-room-highlight_result.webp',
  "0A02FF": '/layout/hallway-highlight_result.webp',
  "B402FE": '/layout/performances-highlight_result.webp',
};

const ROOM_KEY_MAP = {
    "E601FF": "bedroom",
    "FE7902": "living",
    "3CFE02": "bts",
    "02CEFF": "dining",
    "0A02FF": "hallway",
    "B402FE": "performances",
};

const COMPONENT_MAP = {
    "E601FF": Bedroom,
    "FE7902": LivingRoom,
    "3CFE02": BTS,
    "02CEFF": DiningRoom,
    "0A02FF": Hallway,
    "B402FE": Performances,
};

export default function Floorplan({ roomData, activeRoom, setActiveRoom }) {
    const [hoveredRoom, setHoveredRoom] = useState(null);
    const svgRef = useRef(null);
    const [floorPlanLoaded, setFloorPlanLoaded] = useState(false);

    // --- preload all floorplan related images ---
    const floorplanImages = useMemo(() => {
        return [
            '/layout/floorplan-base_result.webp',
            ...Object.values(HIGHLIGHT_MAP)
        ];
    }, []);

    usePreloadImages(floorplanImages);

    useEffect(() => {
        if (!svgRef.current) return;
        const shapes = svgRef.current.querySelectorAll("path, rect, polygon, circle");

        shapes.forEach((shape) => {
            const fill = shape.getAttribute("fill")?.replace("#", "");
            if (!HIGHLIGHT_MAP[fill]) return;

            shape.style.cursor = "pointer";

            shape.addEventListener("mouseenter", () => setHoveredRoom(fill));
            shape.addEventListener("mouseleave", () => setHoveredRoom(null));
            shape.addEventListener("click", () => {
                const roomKey = ROOM_KEY_MAP[fill];
                if (!roomKey) return;
                setActiveRoom((prevRoom) => (prevRoom === roomKey ? null : roomKey));
            });
        });

        return () => {
            shapes.forEach((shape) => {
                const clone = shape.cloneNode(true);
                shape.parentNode.replaceChild(clone, shape);
            });
        };
    }, [setActiveRoom]);

    const highlightImage =
        hoveredRoom || activeRoom
            ? HIGHLIGHT_MAP[
                hoveredRoom
                    ? hoveredRoom
                    : Object.keys(ROOM_KEY_MAP).find(
                        (key) => ROOM_KEY_MAP[key] === activeRoom
                    )
            ]
            : null;

    const ActiveComponent = activeRoom ? COMPONENT_MAP[activeRoom] : null;
    const data = activeRoom ? roomData[activeRoom] : null;

    return (
        <div className="floorplan-container">
            <div className="floorplan-map-wrapper">

                {/* Spinner */}
                {!floorPlanLoaded && (
                    <div className="spinner-wrapper">
                        <div className="spinner" />
                    </div>
                )}

                {/* Base Floorplan */}
                <img
                    onLoad={() => setFloorPlanLoaded(true)}
                    src={'/layout/floorplan-base.png'}
                    alt="Floorplan"
                    loading="lazy"
                    className="floorplan-base"
                />

                {/* Highlight Overlay */}
                {highlightImage && (
                    <img
                        src={highlightImage}
                        loading="eager"
                        alt="Highlighted Room"
                        className="highlight-image"
                    />
                )}

                {/* Invisible SVG */}
                <FloorplanSVG
                    ref={svgRef}
                    className="floorplan-svg-layer"
                />
            </div>

            <div className="room-component-container" style={{ marginTop: 20 }}>
                {ActiveComponent && <ActiveComponent data={data} />}
            </div>
        </div>
    );
}
